import { useState } from 'react';

/**
 * Noticing a prop change DURING the render it arrives in.
 *
 * Both hooks below keep the earlier value in STATE, not a ref. Under StrictMode
 * a component renders twice; a ref advanced by the first pass leaves the second
 * pass comparing a value against itself, so the change is silently lost. A
 * state update during render is the supported form: React re-runs the component
 * immediately with the new state, the condition is false the second time, and
 * nothing paints in between — where an effect would react one paint late.
 *
 * Only for changes CAUSED by the incoming value, which therefore settle in one
 * extra pass. Anything that needs the DOM or an external system is an effect.
 */

/**
 * The value from the previous render — `current` on the first one, so a
 * component that mounts with the value already set observes no change. Callers
 * that must see a flip have to be mounted before it happens.
 */
export function usePreviousValue<T>(current: T): T {
  const [previous, setPrevious] = useState(current);
  if (previous !== current) setPrevious(current);
  return previous;
}

/** True from the first render in which `value` was true, and true from then on. */
export function useLatchedTrue(value: boolean): boolean {
  const [latched, setLatched] = useState(value);
  if (value && !latched) setLatched(true);
  return latched;
}
